// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// CSR Workload Card Component - Individual CSR workload display
// Date: July 26, 2025

import React from 'react';
import { 
  CSRWorkload, 
  EnhancedCSRWorkload,
  getWorkloadLevel, 
  getWorkloadLevelStyles,
  calculateWorkloadBreakdown,
  getCSRAssignmentType,
  getAssignmentTypeLabel
} from '../../utils/csrAssignment';

interface CSRWorkloadCardProps {
  workload: CSRWorkload;
  onClick?: () => void;
  isSelected?: boolean;
  className?: string;
  showBreakdown?: boolean;
}

const CSRWorkloadCard: React.FC<CSRWorkloadCardProps> = ({ 
  workload, 
  onClick, 
  isSelected = false, 
  className = '',
  showBreakdown = true
}) => {
  const workloadLevel = getWorkloadLevel(workload.total_workload_score);
  const levelStyles = getWorkloadLevelStyles(workloadLevel);
  
  // Check if this is enhanced workload data
  const isEnhanced = 'assigned_companies' in workload;
  const enhancedWorkload = workload as EnhancedCSRWorkload;
  const assignmentType = isEnhanced ? getCSRAssignmentType(enhancedWorkload) : 'campaign';
  const hasCompanyAssignments = isEnhanced && enhancedWorkload.assigned_companies > 0;
  
  // Calculate workload breakdown if enhanced data is available
  const breakdown = isEnhanced ? calculateWorkloadBreakdown(enhancedWorkload) : null;

  // Format last active date
  const formatLastActive = (dateString?: string): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`
        bg-white p-4 rounded-lg shadow border transition-all duration-200 hover:shadow-md
        ${isSelected ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'}
        ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}
        ${className}
      `}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `Select CSR ${workload.csr_email}` : undefined}
    >
      {/* Header with CSR info and workload indicator */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate text-sm">
            {workload.csr_name || workload.csr_email}
          </h4>
          {workload.csr_name && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {workload.csr_email}
            </p>
          )}
          {hasCompanyAssignments && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 mt-1">
              🏢 Company CSR
            </span>
          )}
        </div>
        <span className={`
          inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 flex-shrink-0
          ${levelStyles}
        `}>
          {workloadLevel.charAt(0).toUpperCase() + workloadLevel.slice(1)}
        </span>
      </div>

      {/* Enhanced workload metrics */}
      {isEnhanced && showBreakdown ? (
        <div className="space-y-3 mb-3">
          {/* Companies */}
          {enhancedWorkload.assigned_companies > 0 && (
            <div className="bg-blue-50 p-2 rounded border">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-blue-900">
                  🏢 {enhancedWorkload.assigned_companies} {enhancedWorkload.assigned_companies === 1 ? 'Company' : 'Companies'}
                </div>
                <div className="text-sm font-bold text-blue-900">
                  {breakdown?.company_workload_score} pts
                </div>
              </div>
              <div className="text-xs text-blue-700 mt-1">
                {enhancedWorkload.company_campaigns} total campaigns • 5× weight
              </div>
            </div>
          )}
          
          {/* Individual Campaigns */}
          {enhancedWorkload.assigned_campaigns > 0 && (
            <div className="bg-green-50 p-2 rounded border">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-green-900">
                  📋 {enhancedWorkload.assigned_campaigns} Individual Campaign{enhancedWorkload.assigned_campaigns !== 1 ? 's' : ''}
                </div>
                <div className="text-sm font-bold text-green-900">
                  {breakdown?.campaign_workload_score} pts
                </div>
              </div>
              <div className="text-xs text-green-700 mt-1">
                Direct assignments • 3× weight
              </div>
            </div>
          )}
          
          {/* Lead Responses */}
          {enhancedWorkload.active_lead_responses > 0 && (
            <div className="bg-orange-50 p-2 rounded border">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium text-orange-900">
                  💬 {enhancedWorkload.active_lead_responses} Lead Response{enhancedWorkload.active_lead_responses !== 1 ? 's' : ''}
                </div>
                <div className="text-sm font-bold text-orange-900">
                  {breakdown?.lead_response_score} pts
                </div>
              </div>
              <div className="text-xs text-orange-700 mt-1">
                Active responses • 1× weight
              </div>
            </div>
          )}
        </div>
      ) : (
        // Fallback to basic display
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {workload.assigned_campaigns}
            </div>
            <div className="text-xs text-gray-500">
              Campaign{workload.assigned_campaigns !== 1 ? 's' : ''}
            </div>
          </div>
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900">
              {workload.active_lead_responses}
            </div>
            <div className="text-xs text-gray-500">
              Lead Response{workload.active_lead_responses !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      )}

      {/* Total workload score and additional info */}
      <div className="pt-3 border-t border-gray-100">
        <div className="flex justify-between items-center text-xs text-gray-600 mb-1">
          <span className="font-semibold text-gray-900">
            Total Score: {workload.total_workload_score}
          </span>
          <span title={`Last active: ${formatLastActive(workload.csr_last_active)}`}>
            {formatLastActive(workload.csr_last_active)}
          </span>
        </div>
        {isEnhanced && assignmentType !== 'unassigned' && (
          <div className="text-xs text-gray-500">
            Assignment type: {getAssignmentTypeLabel(assignmentType)}
          </div>
        )}
      </div>

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-2 right-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
          </div>
        </div>
      )}
    </div>
  );
};

// Mobile-optimized version for smaller screens
export const MobileCSRWorkloadCard: React.FC<CSRWorkloadCardProps> = ({ 
  workload, 
  onClick, 
  isSelected = false, 
  className = '' 
}) => {
  const workloadLevel = getWorkloadLevel(workload.total_workload_score);
  const levelStyles = getWorkloadLevelStyles(workloadLevel);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      className={`
        bg-white p-3 rounded-lg shadow border transition-all duration-200
        ${isSelected ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'}
        ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}
        ${className}
      `}
      onClick={handleClick}
    >
      {/* Compact header */}
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-medium text-gray-900 text-sm truncate flex-1">
          {workload.csr_name || workload.csr_email}
        </h4>
        <span className={`
          inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ml-2
          ${levelStyles}
        `}>
          {workloadLevel.charAt(0).toUpperCase()}
        </span>
      </div>

      {/* Compact metrics */}
      <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
        <div className="text-center">
          <div className="font-medium text-gray-900">{workload.assigned_campaigns}</div>
          <div>Campaigns</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-gray-900">{workload.active_lead_responses}</div>
          <div>Leads</div>
        </div>
        <div className="text-center">
          <div className="font-medium text-gray-900">{workload.total_workload_score}</div>
          <div>Score</div>
        </div>
      </div>
    </div>
  );
};

export default CSRWorkloadCard;